import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { XCircle } from 'lucide-react'
import ProfileCard from '@/components/ProfileCard'
import ExchangeContactsModal from '@/components/ExchangeContactsModal'
import InstantConnectButton from '@/components/InstantConnectButton'
import PageShell from '@/components/PageShell'
import Spinner from '@/components/Spinner'
import StateMessage from '@/components/StateMessage'
import { validateToken, submitExchange, ApiError } from '@/services/api'
import { logger } from '@/utils/logger'
import { STRINGS } from '@/constants'
import type { ExchangeFormData, OwnerProfile } from '@/types'

const CTX = 'QrLandingPage'

type PageState = 'loading' | 'valid' | 'invalid'

export default function QrLandingPage() {
  const { token = '' } = useParams<{ token: string }>()
  const navigate = useNavigate()
  const [state, setState] = useState<PageState>('loading')
  const [owner, setOwner] = useState<OwnerProfile | null>(null)
  const [modalOpen, setModalOpen] = useState(false)

  useEffect(() => {
    logger.info(CTX, 'mount', { token })

    validateToken(token)
      .then((result) => {
        logger.info(CTX, 'token validation result', result)
        if (result.valid && result.owner) {
          setOwner(result.owner)
          setState('valid')
        } else {
          logger.warn(CTX, 'token invalid or missing owner')
          setState('invalid')
        }
      })
      .catch((err) => {
        logger.error(CTX, 'token validation failed', err)
        setState('invalid')
      })
  }, [token])

  const handleSubmit = async (data: ExchangeFormData) => {
    logger.info(CTX, 'exchange form submit', { token })
    try {
      const { profileAccessToken } = await submitExchange(token, data)
      logger.info(CTX, 'exchange success, redirecting to profile', {
        profileAccessToken,
      })
      if (profileAccessToken) {
        navigate(`/profile/${profileAccessToken}`)
      }
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) {
        throw new Error(STRINGS.qr.errorRateLimit, { cause: err })
      }
      throw new Error(STRINGS.qr.errorGeneric, { cause: err })
    }
  }

  if (state === 'loading') {
    return (
      <PageShell>
        <Spinner label={STRINGS.qr.loading} />
      </PageShell>
    )
  }

  if (state === 'invalid') {
    return (
      <PageShell>
        <StateMessage
          tone="gray"
          icon={<XCircle className="w-7 h-7" strokeWidth={1.5} />}
          title={STRINGS.qr.linkInactive}
          description={STRINGS.qr.linkInactiveDesc}
        />
      </PageShell>
    )
  }

  return (
    <PageShell>
      <div className="page-content">
        {owner && <ProfileCard owner={owner} />}

        <div className="page-cta-stack">
          <button
            onClick={() => {
              logger.debug(CTX, 'exchange contacts modal open')
              setModalOpen(true)
            }}
            className="btn btn-primary"
          >
            {STRINGS.qr.exchangeContacts}
          </button>
          <InstantConnectButton token={token} />
        </div>
      </div>

      {modalOpen && (
        <ExchangeContactsModal
          onClose={() => {
            logger.debug(CTX, 'exchange contacts modal close')
            setModalOpen(false)
          }}
          onSubmit={handleSubmit}
        />
      )}
    </PageShell>
  )
}
